import {
  Box,
  styled,
  Typography,
  FormControl,
  Autocomplete,
  TextField,
  Button,
} from '@mui/material';
import { useState } from 'react';
import { toast } from 'react-toastify';
import { useEnv } from 'src/context/EnvProvider';
import useLoadDataList from 'src/hook/useLoadDataList';
import { getGranits } from 'src/services/granits';
import { addStock, GetPiece, getPieces } from 'src/services/stock';
import { handleMutation } from 'src/utils/api.utils';

export const AddPieceStock = () => {
  const { data: granits } = useLoadDataList(getGranits);
  const { buckets } = useEnv();

  const [selectedPiece, setSelectedPiece] = useState<GetPiece>();
  const [selectedGranitId, setSelectedGranitId] = useState<string>();

  const { data: pieces, onRefresh, refreshing: isLoadingStockPieces } = useLoadDataList(getPieces);

  const [dimensions, setDimensions] = useState<{
    x: number;
    y: number;
    z: number;
  }>({ x: 0, y: 0, z: 0 });

  const [quantity, setQuantity] = useState('');
  const [price, setPrice] = useState('');
  const [name, setName] = useState('');

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();

    if (!selectedPiece || !selectedGranitId) return;

    handleMutation(
      (data) => addStock(data),
      {
        name,
        quantity: Number.parseInt(quantity, 10),
        price: Number.parseFloat(price),
        stockPieces: [
          {
            pieceId: selectedPiece.id,
            granitId: selectedGranitId,
            newGeometry: [],
            x: dimensions.x,
            y: dimensions.y,
            z: dimensions.z,
          },
        ],
      },
      () => {
        toast.success('Stock ajouté avec succès');
        onRefresh();
      },
    );
  };

  return (
    <Container>
      <Form onSubmit={handleSubmit}>
        <FormControl fullWidth sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Row>
            <Autocomplete
              sx={{ width: '100%' }}
              options={pieces || []}
              value={selectedPiece || null}
              onChange={(event, newValue) => {
                setSelectedPiece(newValue || undefined);
                setName(newValue?.name || '');
                setSelectedGranitId(undefined);
              }}
              getOptionLabel={(option) => option.name || ''}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Choisir une pièce"
                  required
                  placeholder="Rechercher une pièce..."
                />
              )}
              renderOption={(props, piece) => {
                const image = piece.pictureUrl;
                return (
                  <Box
                    component="li"
                    {...props}
                    key={piece.id}
                    sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1.5 }}
                  >
                    {image && (
                      <Box
                        component="img"
                        src={`${buckets?.BUCKET_3D_PIECES || ''}/${image}`}
                        alt={piece.name}
                        sx={{ width: 50, height: 50, objectFit: 'cover', borderRadius: 1 }}
                      />
                    )}
                    <Typography variant="body1">{piece.name}</Typography>
                  </Box>
                );
              }}
              ListboxProps={{
                style: {
                  maxHeight: '300px',
                },
              }}
              noOptionsText="Aucune pièce trouvée"
            />

            <Autocomplete
              sx={{ width: '300px' }}
              options={
                granits?.filter((g) => {
                  if (selectedPiece && selectedPiece?.material !== undefined && g.material !== selectedPiece?.material) {
                    return false;
                  }
                  if (selectedPiece?.pieceFabrication) {
                    const fabricationPiece = selectedPiece?.pieceFabrication.find(
                      (p) => p.fabrication === g.fabrication,
                    );
                    return !!fabricationPiece;
                  }
                  return g.fabrication === 'FRENCH';
                }) || []
              }
              value={granits?.find((g) => g.id === selectedGranitId) || null}
              onChange={(_event, newValue) => {
                setSelectedGranitId(newValue?.id);
              }}
              getOptionLabel={(option) => option.name || ''}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              disabled={!selectedPiece}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Choisir un granit"
                  required
                  placeholder="Rechercher un granit..."
                />
              )}
              renderOption={(props, granit) => (
                <Box
                  component="li"
                  {...props}
                  key={granit.id}
                  sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1.5 }}
                >
                  {granit.image && (
                    <Box
                      component="img"
                      src={`${buckets?.BUCKET_3D_PIECES || ''}/${granit.image}`}
                      alt={granit.name}
                      sx={{ width: 50, height: 50, objectFit: 'cover', borderRadius: 1 }}
                    />
                  )}
                  <Typography variant="body1">{granit.name}</Typography>
                </Box>
              )}
              ListboxProps={{
                style: {
                  maxHeight: '300px',
                },
              }}
              noOptionsText="Aucun granit trouvé"
            />
          </Row>

          <Row>
            <TextField
              label="Longueur"
              type="string"
              value={dimensions.z === 0 ? '' : dimensions.z}
              onChange={(e) => setDimensions({ ...dimensions, z: Number(e.target.value) })}
            />
            <TextField
              label="Largeur"
              type="string"
              value={dimensions.x === 0 ? '' : dimensions.x}
              onChange={(e) => setDimensions({ ...dimensions, x: Number(e.target.value) })}
            />
            <TextField
              label="Hauteur"
              type="string"
              value={dimensions.y === 0 ? '' : dimensions.y}
              onChange={(e) => setDimensions({ ...dimensions, y: Number(e.target.value) })}
            />
          </Row>
        </FormControl>

        <TextField
          fullWidth
          label="Quantité"
          type="number"
          value={quantity}
          onChange={(e) => setQuantity(e.target.value)}
          required
          inputProps={{ min: 1 }}
        />

        <TextField
          fullWidth
          label="Prix"
          type="number"
          value={price}
          onChange={(e) => setPrice(e.target.value)}
          required
          inputProps={{ min: 0, step: 0.01 }}
        />

        <TextField
          fullWidth
          label="Nom"
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
        />

        <Button type="submit" variant="contained" color="primary" fullWidth>
          Ajouter au stock
        </Button>
      </Form>
    </Container>
  );
};

const Container = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
});

const Form = styled('form')({
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
});

const Row = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: 16,
});
